import { EventBus } from '@/event-bus';
import Trimaster from '@/plugins/servers/trimaster';
import { createDisplayRecord, onVfuDataChange } from '@/services/dashboardDisplayUtilities';

const state = {
  overlayDeltaX: null,
  overlayDeltaY: null,
  isLoadingSearchResults: false,
  searchResultsTotalCount: 0,
  searchResultUnits: [],
  searchParams: {
    facility_id: null,
    area_id: null,
    dashboardDisplayType_id: null,
    search: null,
  },
  activeSearchLevel: null,
  activeSearchId: null,
  intensitySettings: 0,
  impairedChillerSettings: 0,
  selectedContextMenuItem: null,
  selectedUnits: [],
  selectedBatchAction: null,
  selectedController: null,
  isPerformingContextMenuAction: false,
  showBatchIntensityOptions: false,
  showBatchImpairedChillerOptions: false,
  showSterilizeOptions: false,
  showDeviceHistory: false,
  showDeviceConfigurationViewer: false,
  showAssemblyTests: false,
  showSetDnD: false,
  showAlerts: false,
  showBatchIrrigationOptions: false,
  showBatchCo2CalibrationOptions: false,
  co2CalibrationSettings: {
    ppm: null,
  },
  showBatchControlLoopOptions: false,
  isUpdatingHostname: false,
  isUpgradingFirmware: false,
  showBashCommands: false,
  isMovingPlatformGroup: false,
  isAssigningPlatformGroupCollection: false,
  isUnassigningPlatformGroupCollection: false,
  platformGroupCollectionOptions: [],
  filterPlatformGroupCollection: null,
  controlLoopSettings: null,
  bashSettings: null,
  isChangingController: false,
  showDehumidifyOptions: false,
  showCurtainsConfirm: false,
  selectedCurtainsAction: null,
  showEcuOptions: false,
  showComplianceDialog: false,
  microclimateParams: {
    controller_id: null,
    lifecycle_id: null,
    facility_id: null,
    platformGroupName: null,
  },
  showActiveSoilSensors: false,
  irrigationSettings: {},
  showStartLifecycle: false,
  showVFUSchedule: false,
  useVFUSchedule: false,
};

const getters = {
  overlayDeltaX: (state) => state.overlayDeltaX,
  overlayDeltaY: (state) => state.overlayDeltaY,
  searchResultUnits: (state) => state.searchResultUnits,
  searchResultsTotalCount: (state) => state.searchResultsTotalCount,
  searchParams: (state) => state.searchParams,
  isLoadingSearchResults: (state) => state.isLoadingSearchResults,
  activeSearch: (state) => {
    return {
      activeSearchLevel: state.activeSearchLevel,
      activeSearchId: state.activeSearchId,
    };
  },
  intensitySettings: (state) => state.intensitySettings,
  selectedContextMenuItem: (state) => state.selectedContextMenuItem,
  selectedUnits: (state) => state.selectedUnits,
  selectedBatchAction: (state) => state.selectedBatchAction,
  selectedController: (state) => state.selectedController,
  isPerformingContextMenuAction: (state) => state.isPerformingContextMenuAction,
  showBatchIntensityOptions: (state) => state.showBatchIntensityOptions,
  showBatchImpairedChillerOptions: (state) => state.showBatchImpairedChillerOptions,
  showDeviceHistory: (state) => state.showDeviceHistory,
  showDeviceConfigurationViewer: (state) => state.showDeviceConfigurationViewer,
  showAssemblyTests: (state) => state.showAssemblyTests,
  showSetDnD: (state) => state.showSetDnD,
  showAlerts: (state) => state.showAlerts,
  showBatchIrrigationOptions: (state) => state.showBatchIrrigationOptions,
  showBatchCo2CalibrationOptions: (state) => state.showBatchCo2CalibrationOptions,
  co2CalibrationSettings: (state) => state.co2CalibrationSettings,
  showBatchControlLoopOptions: (state) => state.showBatchControlLoopOptions,
  isUpdatingHostname: (state) => state.isUpdatingHostname,
  isUpgradingFirmware: (state) => state.isUpgradingFirmware,
  showBashCommands: (state) => state.showBashCommands,
  isMovingPlatformGroup: (state) => state.isMovingPlatformGroup,
  isAssigningPlatformGroupCollection: (state) => state.isAssigningPlatformGroupCollection,
  isUnassigningPlatformGroupCollection: (state) => state.isUnassigningPlatformGroupCollection,
  platformGroupCollectionOptions: (state) => state.platformGroupCollectionOptions,
  filterPlatformGroupCollection: (state) => state.filterPlatformGroupCollection,
  controlLoopSettings: (state) => state.controlLoopSettings,
  bashSettings: (state) => state.bashSettings,
  isChangingController: (state) => state.isChangingController,
  showDehumidifyOptions: (state) => state.showDehumidifyOptions,
  showCurtainsConfirm: (state) => state.showCurtainsConfirm,
  selectedCurtainsAction: (state) => state.selectedCurtainsAction,
  showEcuOptions: (state) => state.showEcuOptions,
  showComplianceDialog: (state) => state.showComplianceDialog,
  microclimateParams: (state) => state.microclimateParams,
  showActiveSoilSensors: (state) => state.showActiveSoilSensors,
  irrigationSettings: (state) => state.irrigationSettings,
};

const actions = {
  setOverlayDeltaX({ state }, overlayDeltaX) {
    state.overlayDeltaX = overlayDeltaX;
  },
  setOverlayDeltaY({ state }, overlayDeltaY) {
    state.overlayDeltaY = overlayDeltaY;
  },
  async searchUnits({ state, commit, rootGetters }, searchParams) {
    commit('setIsLoadingSearchResults', true);
    let [response, err] = await Trimaster.get(
      'ag_platformGroups/searchStatusByDisplayType',
      rootGetters.getUser.apitoken,
      searchParams
    );
    commit('setSearchParams', searchParams);
    const platformGroups = response.data.platformGroups.map((platformGroup) =>
      createDisplayRecord(platformGroup, 'VFU')
    );
    commit('setSearchResultsTotalCount', response.data.count);
    commit('setSearchResultUnits', platformGroups);
    commit('setIsLoadingSearchResults', false);
  },
  setActiveSearch({ state, commit }, params) {
    commit('setActiveSearch', params);
  },
  setIntensitySettings({ commit }, intensitySettings) {
    commit('setIntensitySettings', intensitySettings);
  },
  setImpairedChillerSettings({ commit }, impairedChillerSettings) {
    commit('setImpairedChillerSettings', impairedChillerSettings);
  },
  setSelectedContextMenuItem({ commit }, selectedContextMenuItem) {
    commit('setSelectedContextMenuItem', selectedContextMenuItem);
  },
  setSelectedUnits({ commit }, selectedUnits) {
    commit('setSelectedUnits', selectedUnits);
  },
  setSelectedBatchAction({ commit }, selectedBatchAction) {
    commit('setSelectedBatchAction', selectedBatchAction);
  },
  setSelectedController({ commit }, selectedController) {
    commit('setSelectedController', selectedController);
  },
  setIsPerformingContextMenuAction({ commit }, isPerformingContextMenuAction) {
    commit('setIsPerformingContextMenuAction', isPerformingContextMenuAction);
  },
  setShowBatchIntensityOptions({ commit }, showBatchIntensityOptions) {
    commit('setShowBatchIntensityOptions', showBatchIntensityOptions);
  },
  setShowBatchImpairedChillerOptions({ commit }, showBatchImpairedChillerOptions) {
    commit('setShowBatchImpairedChillerOptions', showBatchImpairedChillerOptions);
  },
  setShowSterilizeOptions({ commit }, showSterilizeOptions) {
    commit('setShowSterilizeOptions', showSterilizeOptions);
  },
  setShowDeviceHistory({ commit }, showDeviceHistory) {
    commit('setShowDeviceHistory', showDeviceHistory);
  },
  setShowDeviceConfigurationViewer({ commit }, showDeviceConfigurationViewer) {
    commit('setShowDeviceConfigurationViewer', showDeviceConfigurationViewer);
  },
  setShowAssemblyTests({ commit }, showAssemblyTests) {
    commit('setShowAssemblyTests', showAssemblyTests);
  },
  setShowSetDnD({ commit }, showSetDnD) {
    commit('setShowSetDnD', showSetDnD);
  },
  setShowAlerts({ commit }, showAlerts) {
    commit('setShowAlerts', showAlerts);
  },
  setShowBatchIrrigationOptions({ commit }, showBatchIrrigationOptions) {
    commit('setShowBatchIrrigationOptions', showBatchIrrigationOptions);
  },
  setShowBatchCo2CalibrationOptions({ commit }, showBatchCo2CalibrationOptions) {
    commit('setShowBatchCo2CalibrationOptions', showBatchCo2CalibrationOptions);
  },
  setCo2CalibrationSettings({ commit }, co2CalibrationSettings) {
    commit('setCo2CalibrationSettings', co2CalibrationSettings);
  },
  setShowBatchControlLoopOptions({ commit }, showBatchControlLoopOptions) {
    commit('setShowBatchControlLoopOptions', showBatchControlLoopOptions);
  },
  setIsUpdatingHostname({ commit }, isUpdatingHostname) {
    commit('setIsUpdatingHostname', isUpdatingHostname);
  },
  setIsUpgradingFirmware({ commit }, isUpgradingFirmware) {
    commit('setIsUpgradingFirmware', isUpgradingFirmware);
  },
  setShowBashCommands({ commit }, showBashCommands) {
    commit('setShowBashCommands', showBashCommands);
  },
  setIsMovingPlatformGroup({ commit }, isMovingPlatformGroup) {
    commit('setIsMovingPlatformGroup', isMovingPlatformGroup);
  },
  setIsAssigningPlatformGroupCollection({ commit }, isAssigningPlatformGroupCollection) {
    commit('setIsAssigningPlatformGroupCollection', isAssigningPlatformGroupCollection);
  },
  setPlatformGroupCollectionOptions({ commit }, platformGroupCollectionOptions) {
    commit('setPlatformGroupCollectionOptions', platformGroupCollectionOptions);
  },
  setFilterPlatformGroupCollection({ commit }, filterPlatformGroupCollection) {
    commit('setFilterPlatformGroupCollection', filterPlatformGroupCollection);
  },
  setIsUnassigningPlatformGroupCollection({ commit }, isUnassigningPlatformGroupCollection) {
    commit('setIsUnassigningPlatformGroupCollection', isUnassigningPlatformGroupCollection);
  },
  setControlLoopSettings({ commit }, controlLoopSettings) {
    commit('setControlLoopSettings', controlLoopSettings);
  },
  setBashSettings({ commit }, bashSettings) {
    commit('setBashSettings', bashSettings);
  },
  setIsChangingController({ commit }, isChangingController) {
    commit('setIsChangingController', isChangingController);
  },
  setShowDehumidifyOptions({ commit }, showDehumidifyOptions) {
    commit('setShowDehumidifyOptions', showDehumidifyOptions);
  },
  setShowCurtainsConfirm({ commit }, showCurtainsConfirm) {
    commit('setShowCurtainsConfirm', showCurtainsConfirm);
  },
  setSelectedCurtainsAction({ commit }, selectedCurtainsAction) {
    commit('setSelectedCurtainsAction', selectedCurtainsAction);
  },
  setShowEcuOptions({ commit }, showEcuOptions) {
    commit('setShowEcuOptions', showEcuOptions);
  },
  setShowComplianceDialog({ commit }, showComplianceDialog) {
    commit('setShowComplianceDialog', showComplianceDialog);
  },
  setMicroclimateParams({ commit }, microclimateParams) {
    commit('setMicroclimateParams', microclimateParams);
  },
  setShowActiveSoilSensors({ commit }, showActiveSoilSensors) {
    commit('setShowActiveSoilSensors', showActiveSoilSensors);
  },
  setIrrigationSettings({ commit }, irrigationSettings) {
    commit('setIrrigationSettings', irrigationSettings);
  },
  setShowStartLifecycle({ commit }, showStartLifecycle) {
    commit('setShowStartLifecycle', showStartLifecycle);
  },
  setShowVFUSchedule({ commit }, showVFUSchedule) {
    commit('setShowVFUSchedule', showVFUSchedule);
  },
  setUseVFUSchedule({ commit }, useVFUSchedule) {
    commit('setUseVFUSchedule', useVFUSchedule);
  },
};

const mutations = {
  setIsLoadingSearchResults(state, isLoadingSearchResults) {
    state.isLoadingSearchResults = isLoadingSearchResults;
  },
  setSearchParams(state, searchParams) {
    state.searchParams.facility_id = searchParams.facility_id ?? null;
    state.searchParams.area_id = searchParams.area_id ?? null;
    state.searchParams.dashboardDisplayType_id = searchParams.dashboardDisplayType_id ?? null;
    state.searchParams.search = searchParams.search ?? null;
  },
  setSearchResultsTotalCount(state, count) {
    state.searchResultsTotalCount = count;
  },
  setSearchResultUnits(state, units) {
    state.searchResultUnits = units;
  },
  setActiveSearch(state, params) {
    state.activeSearchLevel =
      typeof params.activeSearchLevel === 'undefined' ? state.activeSearchLevel : params.activeSearchLevel;
    state.activeSearchId = typeof params.activeSearchId === 'undefined' ? state.activeSearchId : params.activeSearchId;
  },
  setIntensitySettings(state, intensitySettings) {
    state.intensitySettings = intensitySettings;
  },
  setImpairedChillerSettings(state, impairedChillerSettings) {
    state.impairedChillerSettings = impairedChillerSettings;
  },
  setSelectedContextMenuItem(state, selectedContextMenuItem) {
    state.selectedContextMenuItem = selectedContextMenuItem;
  },
  setSelectedUnits(state, selectedUnits) {
    state.selectedUnits = selectedUnits;
  },
  setSelectedBatchAction(state, selectedBatchAction) {
    state.selectedBatchAction = selectedBatchAction;
  },
  setSelectedController(state, selectedController) {
    state.selectedController = selectedController;
  },
  setIsPerformingContextMenuAction(state, isPerformingContextMenuAction) {
    state.isPerformingContextMenuAction = isPerformingContextMenuAction;
  },
  setShowBatchIntensityOptions(state, showBatchIntensityOptions) {
    state.showBatchIntensityOptions = showBatchIntensityOptions;
  },
  setShowBatchImpairedChillerOptions(state, showBatchImpairedChillerOptions) {
    state.showBatchImpairedChillerOptions = showBatchImpairedChillerOptions;
  },
  setShowSterilizeOptions(state, showSterilizeOptions) {
    state.showSterilizeOptions = showSterilizeOptions;
  },
  setShowDeviceHistory(state, showDeviceHistory) {
    state.showDeviceHistory = showDeviceHistory;
  },
  setShowDeviceConfigurationViewer(state, showDeviceConfigurationViewer) {
    state.showDeviceConfigurationViewer = showDeviceConfigurationViewer;
  },
  setShowAssemblyTests(state, showAssemblyTests) {
    state.showAssemblyTests = showAssemblyTests;
  },
  setShowSetDnD(state, showSetDnD) {
    state.showSetDnD = showSetDnD;
  },
  setShowAlerts(state, showAlerts) {
    state.showAlerts = showAlerts;
  },
  setShowBatchIrrigationOptions(state, showBatchIrrigationOptions) {
    state.showBatchIrrigationOptions = showBatchIrrigationOptions;
  },
  setShowBatchCo2CalibrationOptions(state, showBatchCo2CalibrationOptions) {
    state.showBatchCo2CalibrationOptions = showBatchCo2CalibrationOptions;
  },
  setCo2CalibrationSettings(state, co2CalibrationSettings) {
    state.co2CalibrationSettings = co2CalibrationSettings;
  },
  setShowBatchControlLoopOptions(state, showBatchControlLoopOptions) {
    state.showBatchControlLoopOptions = showBatchControlLoopOptions;
  },
  setIsUpdatingHostname(state, isUpdatingHostname) {
    state.isUpdatingHostname = isUpdatingHostname;
  },
  setIsUpgradingFirmware(state, isUpgradingFirmware) {
    state.isUpgradingFirmware = isUpgradingFirmware;
  },
  setShowBashCommands(state, showBashCommands) {
    state.showBashCommands = showBashCommands;
  },
  setIsMovingPlatformGroup(state, isMovingPlatformGroup) {
    state.isMovingPlatformGroup = isMovingPlatformGroup;
  },
  setIsAssigningPlatformGroupCollection(state, isAssigningPlatformGroupCollection) {
    state.isAssigningPlatformGroupCollection = isAssigningPlatformGroupCollection;
  },
  setPlatformGroupCollectionOptions(state, platformGroupCollectionOptions) {
    state.platformGroupCollectionOptions = platformGroupCollectionOptions;
  },
  setFilterPlatformGroupCollection(state, filterPlatformGroupCollection) {
    state.filterPlatformGroupCollection = filterPlatformGroupCollection;
  },
  setIsUnassigningPlatformGroupCollection(state, isUnassigningPlatformGroupCollection) {
    state.isUnassigningPlatformGroupCollection = isUnassigningPlatformGroupCollection;
  },
  setControlLoopSettings(state, controlLoopSettings) {
    state.controlLoopSettings = controlLoopSettings;
  },
  setBashSettings(state, bashSettings) {
    state.bashSettings = bashSettings;
  },
  setIsChangingController(state, isChangingController) {
    state.isChangingController = isChangingController;
  },
  setShowDehumidifyOptions(state, showDehumidifyOptions) {
    state.showDehumidifyOptions = showDehumidifyOptions;
  },
  setShowCurtainsConfirm(state, showCurtainsConfirm) {
    state.showCurtainsConfirm = showCurtainsConfirm;
  },
  setSelectedCurtainsAction(state, selectedCurtainsAction) {
    state.selectedCurtainsAction = selectedCurtainsAction;
  },
  setShowEcuOptions(state, showEcuOptions) {
    state.showEcuOptions = showEcuOptions;
  },
  setShowComplianceDialog(state, showComplianceDialog) {
    state.showComplianceDialog = showComplianceDialog;
  },
  setMicroclimateParams(state, microclimateParams) {
    state.microclimateParams = microclimateParams;
  },
  setShowActiveSoilSensors(state, showActiveSoilSensors) {
    state.showActiveSoilSensors = showActiveSoilSensors;
  },
  setIrrigationSettings(state, irrigationSettings) {
    state.irrigationSettings = irrigationSettings;
  },
  setShowStartLifecycle(state, showStartLifecycle) {
    state.showStartLifecycle = showStartLifecycle;
  },
  setShowVFUSchedule(state, showVFUSchedule) {
    state.showVFUSchedule = showVFUSchedule;
  },
  setUseVFUSchedule(state, useVFUSchedule) {
    state.useVFUSchedule = useVFUSchedule;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
};
